import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';

import myImage from '../public/defaultpic.png';
import './NavigationBar.css';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getProfileCompletionPercentage } from './HomePage/profileCompletionUtils';

// In-memory caches (module scoped)
const artistDataCache = {};
const userDataCache = {};
const CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

const NavigationBar = ({ hideProfile = false, userProfilePic, showHomeInDropdown = false, onlyBrand = false, style, skipArtistFetch = false, artistData = null, skipUserFetch = false, userData = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [openProfile, setOpenProfile] = useState(false);
  const [userRole, setUserRole] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [artist, setArtist] = useState(null);
  // NEW: store artist band (first band owned by artist)
  const [artistBand, setArtistBand] = useState(null);

  // Add a state to force re-render on profile image update
  const [profileImageVersion, setProfileImageVersion] = useState(0);

  useEffect(() => {
    // Add scroll listener for navbar effect
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    // Prefer cached role from localStorage to avoid extra network call
    const storedRole = localStorage.getItem('role');
    if (storedRole) {
      setUserRole(storedRole);
      return; // Skip fetch if we already know the role
    }
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const email = localStorage.getItem('email');
    const userId = localStorage.getItem('userId');
    if (isLoggedIn && email && userId) {
      fetch(`https://api.musicalmeet.com/api/auth/get-role?email=${encodeURIComponent(email)}&userId=${userId}`)
        .then(res => res.json())
        .then(data => { if (data && data.role) { const normalizedRole = data.role.toLowerCase() === 'host' ? 'venue' : data.role; setUserRole(normalizedRole); localStorage.setItem('role', normalizedRole); } })
        .catch(() => setUserRole(null));
    }
  }, []);

  // Fetch user or artist data based on role
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const role = userRole || localStorage.getItem('role');
    const token = localStorage.getItem('token');
    if (!isLoggedIn || !role) return;

    // If we are skipping artist fetch and have artist data from parent, set it and skip network
    if (role === 'artist' && skipArtistFetch && artistData) {
      setArtist(prev => (prev && prev._id === artistData._id ? prev : artistData));
      return; // prevent duplicate call
    }
    // If we are skipping user fetch and have user data from parent, set it and skip network
    if (role === 'user' && skipUserFetch && userData) {
      setUser(prev => (prev && prev._id === userData._id ? prev : userData));
      return; // prevent duplicate call
    }

    const now = Date.now();
    if (role === 'user') {
      const userId = localStorage.getItem('userId');
      if (!userId) return;
      const cached = userDataCache[userId];
      const isCacheValid = cached && (now - cached.fetchedAt) < CACHE_TTL_MS;
      if (isCacheValid && profileImageVersion === 0) {
        setUser(cached.data);
        return;
      }
      fetch(`https://api.musicalmeet.com/api/users/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => {
          if (data.imageUrl && data.imageUrl.includes('randomuser.me/api/portraits/')) data.imageUrl = '';
          setUser(data);
            userDataCache[userId] = { data, fetchedAt: Date.now() };
        })
        .catch(() => setUser(null));
    } else if (role === 'artist') {
      const artistId = localStorage.getItem('artist_id');
      if (!artistId) return;
      const cached = artistDataCache[artistId];
      const isCacheValid = cached && (now - cached.fetchedAt) < CACHE_TTL_MS;
      console.log(isCacheValid)
      console.log(profileImageVersion)
      // If profileImageVersion > 0 we force refetch so user sees updated image
      if (isCacheValid && profileImageVersion === 0) {
        setArtist(cached.data);
        return;
      }

      fetch(`https://api.musicalmeet.com/api/artists/${artistId}`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(res => res.json())
        .then(data => {
          if (data.imageUrl && data.imageUrl.includes('randomuser.me/api/portraits/')) data.imageUrl = '';
          setArtist(data);
          artistDataCache[artistId] = { data, fetchedAt: Date.now() };
        })
        .catch(() => setArtist(null));
    }
  }, [userRole, profileImageVersion, skipArtistFetch, artistData, skipUserFetch, userData]);

  // Keep artist state updated if parent passes newer artistData (e.g., after edits)
  useEffect(() => {
    const role = userRole || localStorage.getItem('role');
    if (role === 'artist' && skipArtistFetch && artistData) {
      setArtist(prev => (artistData && (!prev || prev.updatedAt !== artistData.updatedAt) ? artistData : prev));
    }
    if (role === 'user' && skipUserFetch && userData) {
      setUser(prev => (userData && (!prev || prev.updatedAt !== userData.updatedAt) ? userData : prev));
    }
  }, [artistData, skipArtistFetch, userRole, userData, skipUserFetch]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // NEW: Fetch artist's band once artist info / role is known (simple client filter)
  useEffect(() => {
    const role = userRole || localStorage.getItem('role');
    const isLoggedIn = localStorage.getItem('isLoggedIn') === 'true';
    if (role !== 'artist' || !isLoggedIn) return; // only artists when logged in
    const email = (localStorage.getItem('email') || '').trim();
    const artistId = (localStorage.getItem('artist_id') || '').trim();
    if (!email && !artistId) return;

    let cancelled = false;
    const normalizedEmail = email.toLowerCase();

    const fetchBand = async (reason = 'initial') => {
      try {
        console.debug('[NavBar] Fetching bands for artist (reason):', reason, { normalizedEmail, artistId });
        const res = await fetch('https://api.musicalmeet.com/api/bands');
        if (!res.ok) {
          console.warn('[NavBar] /api/bands failed status', res.status);
          return;
        }
        const data = await res.json();
        if (!Array.isArray(data)) return;
        // Match by multiple possible ownerArtist representations: string email, string ObjectId, populated object with email/_id
        const owned = data.find(b => {
          const owner = b.ownerArtist;
          if (!owner) return false;
          // Direct string comparisons
          if (typeof owner === 'string') {
            // If string looks like ObjectId (24 hex chars) compare to artistId; otherwise treat as email
            const isObjectId = /^[a-fA-F0-9]{24}$/.test(owner);
            if (isObjectId && artistId && owner === artistId) return true;
            if (!isObjectId && email && owner.toLowerCase() === normalizedEmail) return true;
            return false;
          }
          // Populated object case
          if (typeof owner === 'object') {
            if (owner._id && artistId && owner._id === artistId) return true;
            if (owner.id && artistId && owner.id === artistId) return true;
            if (owner.email && email && owner.email.toLowerCase() === normalizedEmail) return true;
          }
          return false;
        });
        if (!cancelled && owned) {
          setArtistBand(prev => prev ? prev : owned);
        }
      } catch (e) {
        console.error('[NavBar] Failed fetching bands', e);
      }
    };

    if (!artistBand) {
      fetchBand('no-cache');
      const retry = setTimeout(() => { if (!artistBand) fetchBand('retry-delay'); }, 4000);
      return () => { cancelled = true; clearTimeout(retry); };
    }
  }, [userRole, artistBand]);

  // Listen for explicit refresh trigger after band creation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'myBandNeedsRefresh') {
        setArtistBand(null); // force refetch in effect above
      }
    };
    window.addEventListener('storage', handler);
    return () => window.removeEventListener('storage', handler);
  }, []);

  // state changes
  const [openDesktopProfile, setOpenDesktopProfile] = useState(false);
  const [openMobileProfile, setOpenMobileProfile] = useState(false);

  // handle click outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMobileMenuOpen && !event.target.closest('.navbar-container') && !event.target.closest('.mobile-menu')) {
        setIsMobileMenuOpen(false);
      }
      if (openDesktopProfile && !event.target.closest('.profile-section')) {
        setOpenDesktopProfile(false);
      }
      if (openMobileProfile && !event.target.closest('.mobile-profile')) {
        setOpenMobileProfile(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isMobileMenuOpen, openDesktopProfile, openMobileProfile]);


  const handleLogout = () => {
    localStorage.removeItem('isLoggedIn');
    localStorage.removeItem('name');
    localStorage.removeItem('email');
    localStorage.removeItem('token');
    localStorage.removeItem('userId');
    localStorage.removeItem('artist_id');
    localStorage.removeItem('venue_id');
    localStorage.removeItem('role'); // ensure role cleared so Create Band does not appear after logout
    navigate('/login');
  };

  const handleuserrequest = () => {
    if (userRole === 'artist') {
      navigate('/eventdashboard');
    }
    else {
      navigate('/userrequests');
    }
  };

  // Navigate to band creation page for artists
  const handleCreateBand = () => {
    if (userRole === 'artist') {
      navigate('/create-band');
      setOpenDesktopProfile(false);
      setOpenMobileProfile(false);
      setIsMobileMenuOpen(false);
    }
  };

  const HandleArtistClick = (e) => {
    e.preventDefault();
    const section = document.getElementById('featured-artists');
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate('/all-artists');
    }
  };

  const isArtistProfilePage = location.pathname.startsWith('/artist-profile/');
  const isLoginOrSignup = location.pathname === '/login' || location.pathname === '/signup';

  const getImageSrc = (url) => {
    if (!url) return null;
    let match = url.match(/(?:file\/d\/|open\?id=|uc\?id=)([\w-]+)/);
    if (!match) {
      match = url.match(/[?&]id=([\w-]+)/);
    }
    let directUrl = url;
    if (match && match[1]) {
      directUrl = `https://drive.google.com/uc?export=view&id=${match[1]}`;
    }
    // Always proxy through backend for CORS
    return `https://api.musicalmeet.com/api/proxy-image?url=${encodeURIComponent(directUrl)}`;
  };

  // Helper to get the correct profile image src
  const getProfilePicSrc = () => {
    if (userRole === 'artist') {
      if (artist && artist.imageUrl) return getImageSrc(artist.imageUrl);
      return myImage;
    } else if (userRole === 'user') {
      if (user && user.imageUrl) return getImageSrc(user.imageUrl);
      return myImage;
    } else if (userRole === 'venue') {
      return myImage;
    }
    return myImage;
  };

  // Profile completion percentage
  let profileCompletion = null;
  if (userRole === 'artist' && artist) {
    profileCompletion = getProfileCompletionPercentage(artist, 'artist');
  } else if (userRole === 'user' && user) {
    profileCompletion = getProfileCompletionPercentage(user, 'user');
  }

  const handleProfileNavigation = async () => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const role = userRole || localStorage.getItem('role');
    const token = localStorage.getItem('token');

    if (role === 'user') {
      const userId = localStorage.getItem('userId');
      if (!userId) {
        toast.error('User ID not found. Please log in again.');
        return;
      }
      navigate(`/ProfilePage/${userId}`);
    } else if (role === 'artist') {
      const artistId = localStorage.getItem('artist_id');
      if (!artistId) {
        toast.error('Artist ID not found. Please log in again.');
        return;
      }

      // Use already-fetched artist state instead of making a second API call
      if (artist) {
        const requiredFields = ['bio', 'description', 'instruments', 'genres', 'pricePerHour'];
        const isProfileComplete = requiredFields.every(field => {
          const value = artist[field];
          return value !== undefined && value !== null && (Array.isArray(value) ? value.length > 0 : value !== '');
        });
        if (!isProfileComplete) {
          navigate(`/publicartistprofilepage/${artistId}`, { state: { incompleteProfile: true } });
        } else {
          navigate(`/publicartistprofilepage/${artistId}`);
        }
      } else {
        // Fallback: if artist data not yet loaded, just navigate without extra fetch
        navigate(`/publicartistprofilepage/${artistId}`);
      }
    } else if (role === 'venue') {
      navigate('/venue-profile');
    }
    setOpenProfile(false);
    setIsMobileMenuOpen(false);
  };

  // NEW: Navigate to artist's band public page
  const handleMyBandNavigation = () => {
    if (artistBand && artistBand._id) {
      navigate(`/edit-band/${artistBand._id}`);
      setOpenDesktopProfile(false);
      setOpenMobileProfile(false);
      setIsMobileMenuOpen(false);
    } else {
      handleCreateBand();
    }
  };

  const handleHomeNavigation = () => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const role = userRole || localStorage.getItem('role');
    if (isLoggedIn && role === 'user') {
      navigate('/loggedInHome');
    } else if (isLoggedIn && role === 'artist') {
      navigate('/loggedInHomePageArtist');
    } else if (isLoggedIn && role === 'venue') {
      navigate('/loggedInHomeVenue');
    } else {
      navigate('/');
    }
    setOpenProfile(false);
    setIsMobileMenuOpen(false);
  };

  const handleNavigation = (path) => {
    navigate(path);
    setIsMobileMenuOpen(false);
  };

  const handleBrandClick = () => {
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const role = localStorage.getItem('role');
    if (isLoggedIn && role === 'user') {
      navigate('/loggedInHome');
    } else if (isLoggedIn && role === 'artist') {
      navigate('/loggedInHomePageArtist');
    } else if (isLoggedIn && role === 'venue') {
      navigate('/loggedInHomeVenue');
    } else {
      navigate('/');
    }
    setIsMobileMenuOpen(false);
  };

  const handleHomeClick = (e) => {
    e.preventDefault();
    handleHomeNavigation();
  };

  // Listen for profile image updates in localStorage (e.g., after upload)
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'profileImageUpdated') {
        setProfileImageVersion(v => v + 1); // Force re-render
      }
    };
    window.addEventListener('storage', handleStorageChange);
    // Also check on mount in case the event doesn't fire in same tab
    if (localStorage.getItem('profileImageUpdated')) {
      setProfileImageVersion(v => v + 1);
      localStorage.removeItem('profileImageUpdated');
    }
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Helper to check if user is logged in
  const isLoggedIn = !!localStorage.getItem('isLoggedIn');

  // NEW: Only show Band link when artist is logged in and not on public landing page
  const showBandNavLink = userRole === 'artist' && isLoggedIn && location.pathname !== '/';

  if (onlyBrand) {
    // Render only the brand text, with transparent background and no extra elements
    return (
      <nav className="navbar only-brand" style={{ background: 'transparent', boxShadow: 'none', ...style, position: 'relative', zIndex: 1005 }}>
        <div className="navbar-container" style={{ justifyContent: 'flex-start' }}>
          <span
            className="navbar-title"
            style={{ cursor: 'pointer' }}
            onClick={() => window.location.replace('/')}
          >
            musical meet
          </span>
        </div>
      </nav>
    );
  }

  return (
    <nav className={`navbar ${isScrolled ? 'navbar-scrolled' : ''}`}>

      {/* ── Floating pill ── */}
<div className="navbar-container">

        {/* Brand */}
        <div className="navbar-brand" onClick={handleBrandClick} style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', flexShrink: 0 }}>
          <div style={{ width: 40, height: 40, background: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
            <img src="/LOGO.avif" alt="Musical Meet" style={{ width: '90%', height: 'auto' }} />
          </div>
          <span style={{ color: '#f1f5f9', fontFamily: 'Gilroy-semiBold', fontSize: '1.1rem', letterSpacing: 0.3, whiteSpace: 'nowrap' }}>musical meet</span>
        </div>

        {/* Desktop Links */}
        <div className="desktop-nav" style={{ alignItems: 'center', gap: '2rem', flex: 1, justifyContent: 'center' }}>
          {location.pathname !== '/' && location.pathname !== '/home' && (
            <a className="nav-link" onClick={handleHomeClick}>Home</a>
          )}
          <a className="nav-link" onClick={(e) => { e.preventDefault(); handleNavigation('/all-artists'); }}>Artists</a>
          <a className="nav-link" onClick={(e) => { e.preventDefault(); handleNavigation('/all-bands'); }}>Bands</a>
          <a className="nav-link" onClick={(e) => { e.preventDefault(); handleNavigation('/all-venues'); }}>Venues</a>
          <a className="nav-link" onClick={(e) => { e.preventDefault(); handleNavigation('/how-it-works'); }}>How It Works</a>
          <a className="nav-link" onClick={(e) => { e.preventDefault(); handleNavigation('/vocal-analysis'); }}>Vocal Analysis</a>
          {showBandNavLink && (
            <a className="nav-link" onClick={(e) => { e.preventDefault(); artistBand ? handleMyBandNavigation() : handleCreateBand(); }}>
              {artistBand ? 'My Band' : 'Create Band'}
            </a>
          )}
        </div>

        {/* Right: CTA */}
        <div className="desktop-cta" style={{ alignItems: 'center', gap: 12, flexShrink: 0 }}>
          {!isLoginOrSignup && isLoggedIn ? (
            <div className="profile-section">
              <div className="profile-container" onClick={() => setOpenDesktopProfile(!openDesktopProfile)}>
                <img src={getProfilePicSrc()} alt="Profile" className="profile-image" />
                {profileCompletion !== null && profileCompletion < 100 && (
                  <span className="completion-badge">{profileCompletion}%</span>
                )}
                {openDesktopProfile && (
                  <div className="profile-dropdown" style={{ opacity: 1, visibility: 'visible', transform: 'translateY(0)' }}>
                    <div className="dropdown-item" onClick={showHomeInDropdown ? handleHomeNavigation : handleProfileNavigation}>
                      {showHomeInDropdown ? 'Home' : userRole === 'venue' ? 'My Venue Profile' : 'My Profile'}
                    </div>
                    {userRole === 'venue' && (
                      <div className="dropdown-item" onClick={() => { navigate('/venue-profile'); setOpenDesktopProfile(false); }}>Add Room</div>
                    )}
                    <div className="dropdown-item" onClick={userRole === 'venue' ? () => navigate('/userrequests') : handleuserrequest}>
                      {userRole === 'venue' ? 'My Bookings' : 'My Event Requests'}
                    </div>
                    <div className="dropdown-item" onClick={handleLogout}>Logout</div>
                  </div>
                )}
              </div>
            </div>
          ) : !isLoginOrSignup && (
            <>
              <button onClick={() => handleNavigation('/login')}
                style={{ background: 'none', border: 'none', color: '#94a3b8', fontFamily: 'Gilroy-semiBold', fontSize: '0.92rem', cursor: 'pointer', padding: '6px 12px', borderRadius: 8, transition: 'color 0.2s' }}
                onMouseEnter={e => e.currentTarget.style.color = '#f1f5f9'}
                onMouseLeave={e => e.currentTarget.style.color = '#94a3b8'}>
                Login
              </button>
              <button onClick={() => handleNavigation('/signup')}
                style={{ background: '#374EA9', border: 'none', color: '#fff', fontFamily: 'Gilroy-semiBold', fontSize: '0.9rem', cursor: 'pointer', padding: '8px 20px', borderRadius: 50, boxShadow: '0 4px 16px rgba(55,78,169,0.4)', transition: 'background 0.2s, transform 0.15s', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#4a62d6'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = '#374EA9'; e.currentTarget.style.transform = 'translateY(0)'; }}>
                Join Free
              </button>
            </>
          )}
        </div>

        {/* Mobile Hamburger */}
        <button className={`hamburger ${isMobileMenuOpen ? 'is-active' : ''}`} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
          <span className="hamburger-line"></span>
        </button>
      </div>

      {/* ── Mobile Menu ── */}
      {isMobileMenuOpen && (
        <div className="mobile-menu" style={{ background: 'rgba(15,23,42,0.98)', borderTop: '1px solid rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', padding: '8px 0 16px' }}>
          <div className="mobile-menu-content">
            {!isLoginOrSignup && isLoggedIn && (
              <div className="mobile-profile">
                <div className="profile-pic-container" onClick={(e) => { e.stopPropagation(); setOpenMobileProfile(!openMobileProfile); }}>
                  <img src={getProfilePicSrc()} alt="Profile" className="mobile-profile-image" />
                  {profileCompletion !== null && profileCompletion < 100 && (
                    <span className="mobile-completion-badge">{profileCompletion}% Complete</span>
                  )}
                </div>
                {openMobileProfile && (
                  <div className="mobile-profile-dropdown">
                    <div className="dropdown-item" onClick={showHomeInDropdown ? handleHomeNavigation : handleProfileNavigation}>
                      {showHomeInDropdown ? 'Home' : userRole === 'venue' ? 'My Venue Profile' : 'My Profile'}
                    </div>
                    {userRole === 'venue' && (
                      <div className="dropdown-item" onClick={() => { navigate('/venue-profile'); setOpenMobileProfile(false); setIsMobileMenuOpen(false); }}>Add Room</div>
                    )}
                    <div className="dropdown-item" onClick={userRole === 'venue' ? () => navigate('/userrequests') : handleuserrequest}>
                      {userRole === 'venue' ? 'My Bookings' : 'My Event Requests'}
                    </div>
                    <div className="dropdown-item" onClick={handleLogout}>Logout</div>
                  </div>
                )}
              </div>
            )}

            <div className="mobile-links">
              {location.pathname !== '/' && location.pathname !== '/home' && (
                <a className="mobile-link" onClick={handleHomeClick}>Home</a>
              )}
              <a className="mobile-link" onClick={(e) => { e.preventDefault(); handleNavigation('/all-artists'); }}>Artists</a>
              <a className="mobile-link" onClick={(e) => { e.preventDefault(); handleNavigation('/all-bands'); }}>Bands</a>
              <a className="mobile-link" onClick={(e) => { e.preventDefault(); handleNavigation('/all-venues'); }}>Venues</a>
              <a className="mobile-link" onClick={(e) => { e.preventDefault(); handleNavigation('/how-it-works'); }}>How It Works</a>
              <a className="mobile-link" onClick={(e) => { e.preventDefault(); handleNavigation('/vocal-analysis'); }}>Vocal Analysis</a>
              {showBandNavLink && (
                <a className="mobile-link" onClick={(e) => { e.preventDefault(); artistBand ? handleMyBandNavigation() : handleCreateBand(); }}>
                  {artistBand ? 'My Band' : 'Create Band'}
                </a>
              )}
            </div>

            {!isLoginOrSignup && !isLoggedIn && (
              <div style={{ padding: '8px 16px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                <button onClick={() => handleNavigation('/login')}
                  style={{ background: 'none', border: '1px solid rgba(255,255,255,0.15)', color: '#94a3b8', fontFamily: 'Gilroy-semiBold', fontSize: '0.95rem', cursor: 'pointer', padding: '10px', borderRadius: 10, width: '100%' }}>
                  Login
                </button>
                <button onClick={() => handleNavigation('/signup')}
                  style={{ background: '#374EA9', border: 'none', color: '#fff', fontFamily: 'Gilroy-semiBold', fontSize: '0.95rem', cursor: 'pointer', padding: '10px', borderRadius: 10, width: '100%', boxShadow: '0 4px 16px rgba(55,78,169,0.3)' }}>
                  Join Free
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default NavigationBar;
